import { MediaItem } from '../types';
import { convertHeicSingleFlight } from './heicWorkerClient';

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

export type ImageFormat = 'avif' | 'webp' | 'jpeg';
export type ImageSize = 320 | 640 | 1024 | 2048;

export interface ProcessedImage {
  format: ImageFormat;
  size: ImageSize;
  blob: Blob;
  url: string; // Object URL
}

export interface ImageMetadata {
  id: string;
  formats: ImageFormat[];
  sizes: ImageSize[];
  originalFormat: string;
  processedAt: number;
  sourceVersion: string;
}

/**
 * Image Processing Service
 * Converts images to multiple formats and sizes per PRD requirements
 */
export const imageProcessingService = {
  /**
   * Process an image: convert HEIC to JPEG/WebP and generate multiple sizes
   * Returns processed images with blob URLs
   */
  async processImage(
    item: MediaItem,
    sourceBlob: Blob,
    accessToken: string | null
  ): Promise<{
    images: ProcessedImage[];
    metadata: ImageMetadata;
  }> {
    if (!isBrowser) {
      throw new Error('Image processing requires a browser environment');
    }

    const isHeic = item.name.toLowerCase().endsWith('.heic') ||
                   item.name.toLowerCase().endsWith('.heif') ||
                   item.mimeType === 'image/heic' ||
                   item.mimeType === 'image/heif' ||
                   item.mimeType === 'image/heic-sequence' ||
                   item.mimeType === 'image/heif-sequence';

    const processedImages: ProcessedImage[] = [];
    const sourceVersion = getSourceVersion(item);

    if (isHeic) {
      try {
        console.log('Converting HEIC to JPEG in worker...');
        const bytes = await sourceBlob.arrayBuffer();
        const { blob } = await convertHeicSingleFlight({
          imageId: item.id,
          bytes,
          maxSize: 1024,
        });

        const url = URL.createObjectURL(blob);
        processedImages.push({
          format: 'jpeg',
          size: 1024,
          blob,
          url,
        });
        console.log('HEIC converted to JPEG');
      } catch (error: any) {
        console.error('HEIC conversion failed:', error);
        // Don't throw - gracefully fall back to using source blob
        if (sourceBlob.type === 'application/octet-stream') {
          sourceBlob = new Blob([sourceBlob], { type: 'image/jpeg' });
        }
      }
    }

    if (processedImages.length === 0) {
      const baseImage = await this.loadImageFromBlob(sourceBlob);
      try {
        const processed = await this.generateImageVariant(
          baseImage,
          'jpeg',
          1024
        );
        processedImages.push(processed);
      } catch (error) {
        console.warn('Failed to generate jpeg 1024w:', error);
      }
    }

    const metadata: ImageMetadata = {
      id: item.id,
      formats: [...new Set(processedImages.map(img => img.format))],
      sizes: [...new Set(processedImages.map(img => img.size))],
      originalFormat: item.mimeType,
      processedAt: Date.now(),
      sourceVersion,
    };

    return { images: processedImages, metadata };
  },

  /**
   * Load a blob into an HTMLImageElement
   */
  async loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
    if (!isBrowser) {
      throw new Error('loadImageFromBlob requires a browser environment');
    }
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      
      img.src = url;
    });
  },

  /**
   * Generate a specific format/size variant of an image
   */
  async generateImageVariant(
    sourceImage: HTMLImageElement,
    format: ImageFormat,
    maxSize: ImageSize
  ): Promise<ProcessedImage> {
    if (!isBrowser) {
      throw new Error('generateImageVariant requires a browser environment');
    }
    // Calculate dimensions maintaining aspect ratio
    let width = sourceImage.width;
    let height = sourceImage.height;
    
    if (width > maxSize || height > maxSize) {
      const ratio = Math.min(maxSize / width, maxSize / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Draw resized image
    ctx.drawImage(sourceImage, 0, 0, width, height);

    // Convert to blob with appropriate quality
    const quality = this.getQualityForFormat(format);
    const mimeType = this.getMimeTypeForFormat(format);
    
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error(`Failed to convert to ${format}`));
          }
        },
        mimeType,
        quality
      );
    });

    const url = URL.createObjectURL(blob);

    return {
      format,
      size: maxSize,
      blob,
      url
    };
  },

  /**
   * Get quality setting for format (per PRD)
   */
  getQualityForFormat(format: ImageFormat): number {
    switch (format) {
      case 'avif':
        return 0.50; // Q ≈ 45-55
      case 'webp':
        return 0.75; // Q ≈ 70-80
      case 'jpeg':
        return 0.80; // Q ≈ 75-85
      default:
        return 0.80;
    }
  },

  /**
   * Get MIME type for format
   */
  getMimeTypeForFormat(format: ImageFormat): string {
    switch (format) {
      case 'avif':
        return 'image/avif';
      case 'webp':
        return 'image/webp';
      case 'jpeg':
        return 'image/jpeg';
      default:
        return 'image/jpeg';
    }
  },

  /**
   * Revoke object URLs to free memory
   */
  revokeUrls(images: ProcessedImage[]): void {
    images.forEach(img => {
      URL.revokeObjectURL(img.url);
    });
  }
};

export const getSourceVersion = (item: MediaItem): string => {
  if (item.source === 'local' && item.file) {
    return String(item.file.lastModified ?? '0');
  }

  return item.driveFile?.id ?? item.id;
};
